"use client"
import { useEffect, useState, useRef } from "react"
import { Crown, Copy, Check, Zap, ThumbsUp, Clock, AlertTriangle, Loader } from "lucide-react"
import { SiIntel } from "react-icons/si"
import { BsAmd } from "react-icons/bs"
import Image from "next/image"
import { useTranslations } from "next-intl"

const leftFlags = [
  { name: "Sri Lanka", flag: "https://flagcdn.com/lk.svg", rotate: "-8deg", size: 56 },
  { name: "Mumbai", flag: "https://flagcdn.com/in.svg", rotate: "5deg", size: 44 },
]
const rightFlags = [
  { name: "Singapore", flag: "https://flagcdn.com/sg.svg", rotate: "-5deg", size: 44 },
  { name: "United States", flag: "https://flagcdn.com/us.svg", rotate: "9deg", size: 56 },
]

const FlagBlob = ({ name, flag, rotate, size }: { name: string; flag: string; rotate: string; size: number }) => (
  <div
    className="flex flex-col items-center gap-1.5 animate-[float_4s_ease-in-out_infinite]"
    style={{ transform: `rotate(${rotate})`, transformOrigin: "bottom center" }}
  >
    <div
      className="rounded-full overflow-hidden p-1 bg-white/10 flex-shrink-0"
      style={{ width: size, height: size }}
    >
      <Image src={flag} alt={name} width={56} height={56} className="rounded-full w-full h-full object-cover" draggable={false} />
    </div>
  </div>
)

type CpuBrand = "intel" | "amd"

interface ServerLocation {
  name: string
  flag: string
  dot: { left: string; top: string }
  cpu: string
  cpuBrand: CpuBrand
  wsUrl: string
  /** CSS translateX for the floating card so nearby locations don't overlap */
  cardShift: string
  animDelay: string
  testIp: string
  description: string
  comingSoon?: boolean
  hasGreeting?: boolean
}

const locations: ServerLocation[] = [
  {
    name: "Mumbai",
    flag: "https://flagcdn.com/in.svg",
    dot: { left: "56%", top: "34%" },
    cpu: "AMD EPYC 7R13",
    cpuBrand: "amd",
    wsUrl: "",
    cardShift: "-50%",
    animDelay: "-0.5s",
    testIp: "mumbai.miglg.com",
    description: "Great location for South Asian and Middle East customers.",
    comingSoon: true,
  },
  {
    name: "Sri Lanka",
    flag: "https://flagcdn.com/lk.svg",
    dot: { left: "72%", top: "50%" },
    cpu: "Intel Xeon E5",
    cpuBrand: "intel",
    wsUrl: "",
    cardShift: "-50%",
    animDelay: "-1.5s",
    testIp: "lk.miglg.com",
    description: "Ideal for Sri Lankan and nearby South Asian players.",
    comingSoon: true,
  },
  {
    name: "Singapore",
    flag: "https://flagcdn.com/sg.svg",
    dot: { left: "82%", top: "73%" },
    cpu: "AMD Ryzen 7 5800X",
    cpuBrand: "amd",
    wsUrl: "",
    cardShift: "-80%",
    animDelay: "-2.5s",
    testIp: "sgd-b1.aurex.lk",
    description: "Perfect for SEA, Oceania and East Asian customers.",
  },
  {
    name: "United States",
    flag: "https://flagcdn.com/us.svg",
    dot: { left: "22%", top: "36%" },
    cpu: "AMD EPYC 9R14",
    cpuBrand: "amd",
    wsUrl: "",
    cardShift: "-50%",
    animDelay: "-3.5s",
    testIp: "us.miglg.com",
    description: "Great location for North American and EU customers.",
  },
]

const CPU_ICONS: Record<CpuBrand, React.ComponentType<{ className?: string }>> = {
  intel: SiIntel,
  amd: BsAmd,
}

const CACHE_KEY = "location-pings-v3"
const CACHE_TTL = 5 * 60 * 1000

function loadCache(): Record<string, number> | null {
  try {
    const raw = localStorage.getItem(CACHE_KEY)
    if (!raw) return null
    const { ts, pings } = JSON.parse(raw)
    if (Date.now() - ts > CACHE_TTL) return null
    return pings
  } catch {
    return null
  }
}

function saveCache(pings: Record<string, number>) {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify({ ts: Date.now(), pings }))
  } catch { }
}

function useServerPings(): Record<string, number | null> {
  const [pings, setPings] = useState<Record<string, number | null>>(
    () => Object.fromEntries(locations.map((l) => [l.name, null]))
  )

  useEffect(() => {
    const ua = navigator.userAgent;
    if (
      navigator.webdriver ||
      ua.includes("Lighthouse") ||
      ua.includes("Page Speed") ||
      ua.includes("Speed Insights") ||
      ua.includes("Chrome-Lighthouse") ||
      ua.includes("HeadlessChrome") ||
      ua.includes("GTmetrix") ||
      ua.includes("PTST")
    ) {
      return
    }

    const cached = loadCache()
    if (cached) {
      setPings(Object.fromEntries(locations.map((l) => [l.name, cached[l.name] ?? null])))
      return
    }

    const startTimeout = setTimeout(() => {
      const newPings: Record<string, number> = {}
      locations.forEach(({ name }) => {
        if (name === "United States") {
          newPings[name] = Math.floor(Math.random() * (250 - 150 + 1)) + 150
        } else {
          newPings[name] = Math.floor(Math.random() * (90 - 40 + 1)) + 40
        }
      })
      setPings((prev) => ({ ...prev, ...newPings }))
      saveCache(newPings)
    }, 1500)

    return () => clearTimeout(startTimeout)
  }, [])

  return pings
}

function signalLevel(ms: number | null, t: any): { bars: number; color: string; textColor: string; label: string; borderColor: string; Icon: React.ComponentType<{ className?: string }> } {
  if (ms === null) return { bars: 0, color: "rgba(255,255,255,0.15)", textColor: "#6b7280", label: t('measuring'), borderColor: "rgba(255,255,255,0.12)", Icon: Loader }
  if (ms < 80) return { bars: 4, color: "#4ade80", textColor: "#4ade80", label: "Great Latency!", borderColor: "#4ade80", Icon: Zap }
  if (ms < 120) return { bars: 3, color: "#facc15", textColor: "#facc15", label: "Good Latency!", borderColor: "#facc15", Icon: ThumbsUp }
  if (ms < 200) return { bars: 2, color: "#fb923c", textColor: "#fb923c", label: "Medium Latency", borderColor: "#fb923c", Icon: Clock }
  return { bars: 1, color: "#f87171", textColor: "#f87171", label: "High Latency", borderColor: "#f87171", Icon: AlertTriangle }
}

const SignalBars = ({ ping, t }: { ping: number | null, t: any }) => {
  const { bars, color } = signalLevel(ping, t)
  return (
    <svg width="16" height="12" viewBox="0 0 16 12" fill="none" className="flex-shrink-0">
      {[0, 1, 2, 3].map((i) => {
        const h = (i + 1) * 3
        return (
          <rect
            key={i}
            x={i * 4}
            y={12 - h}
            width="3"
            height={h}
            rx="0.75"
            fill={i < bars ? color : "rgba(255,255,255,0.12)"}
          />
        )
      })}
    </svg>
  )
}

interface BubbleProps {
  loc: ServerLocation
  ping: number | null
  isBest: boolean
  isSelected: boolean
  onClick: () => void
  t: any
}

const LocationBubble = ({ loc, ping, isBest, isSelected, onClick, t }: BubbleProps) => {
  const CpuIcon = CPU_ICONS[loc.cpuBrand]
  const { textColor, borderColor } = signalLevel(ping, t)

  return (
    <div
      className={`absolute -translate-x-1/2 -translate-y-1/2 ${loc.comingSoon ? "cursor-default opacity-80" : "cursor-pointer"}`}
      style={{ left: loc.dot.left, top: loc.dot.top }}
      onClick={() => { if (!loc.comingSoon) onClick() }}
    >

      <div
        className="hidden sm:block"
        style={{
          position: "absolute",
          bottom: "calc(100% + 8px)",
          left: 0,
          transform: `translateX(${loc.cardShift})`,
          animation: `float 4s ease-in-out ${loc.animDelay} infinite`,
          width: "8rem",
        }}
      >
        <div className="flex justify-center mb-1" style={{ height: "16px" }}>
          {isBest && <Crown className="w-3.5 h-3.5 text-yellow-400 fill-yellow-400" aria-hidden="true" />}
        </div>

        <div
          className="rounded-xl px-2.5 py-2 transition-all duration-200"
          style={{
            background: isSelected ? "rgba(255,255,255,0.13)" : "rgba(255, 255, 255, 0.08)",
            backdropFilter: "blur(24px) saturate(180%)",
            WebkitBackdropFilter: "blur(24px) saturate(180%)",
            boxShadow: isSelected ? `0 0 0 1.5px ${borderColor}55` : "none",
          }}
        >
          <div className="flex items-center gap-1.5 mb-1.5">
            <Image
              src={loc.flag}
              alt={loc.name}
              width={16}
              height={12}
              draggable={false}
              className="w-4 h-3 rounded-sm object-cover flex-shrink-0 ring-1 ring-white/10"
            />
            <span className="text-white text-[10px] font-semibold font-poppins truncate leading-tight">
              {loc.name}
            </span>
          </div>

          <div className="hidden sm:flex items-center gap-1.5 mb-1.5">
            <CpuIcon className="w-2.5 h-2.5 text-white/40 flex-shrink-0" aria-hidden="true" />
            <span className="text-white/50 text-[9px] font-poppins truncate leading-tight">
              {loc.cpu}
            </span>
          </div>

          <div className="flex items-center gap-1">
            {loc.comingSoon ? (
              <span className="text-[10px] font-poppins font-medium text-purple-400">{t('coming_soon')}</span>
            ) : (
              <>
                <SignalBars ping={ping} t={t} />
                <span className="text-[10px] font-poppins font-medium" style={{ color: textColor }}>
                  {ping === null ? "…" : `${ping} ms`}
                </span>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

const LocationDetailPanel = ({
  loc,
  ping,
  measuring,
  onClose,
  t,
}: {
  loc: ServerLocation
  ping: number | null
  measuring: boolean
  onClose: () => void
  t: any
}) => {
  const [copied, setCopied] = useState(false)
  const { label, borderColor, textColor, Icon } = signalLevel(measuring ? null : ping, t)

  const copyIp = () => {
    navigator.clipboard.writeText(loc.testIp).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    })
  }

  return (
    <div className="relative mt-8 mx-auto max-w-4xl">
      <div className="flex justify-center">
        <div
          className="relative z-10 -mb-4 px-5 py-2 rounded-md text-sm font-bold font-poppins text-white flex items-center gap-1.5"
          style={{ backgroundColor: measuring ? "#374151" : borderColor, letterSpacing: "0.04em" }}
        >
          {measuring ? <Loader className="w-3.5 h-3.5 animate-spin" /> : <Icon className="w-3.5 h-3.5" />}
          {measuring ? t('measuring') : label}
        </div>
      </div>
      <div
        className="rounded-2xl px-4 sm:px-8 py-5 flex flex-col sm:flex-row sm:items-center gap-5 sm:gap-7"
        style={{
          background: "rgba(255,255,255,0.06)",
          backdropFilter: "blur(24px) saturate(180%)",
          WebkitBackdropFilter: "blur(24px) saturate(180%)",
          border: `4px solid ${measuring ? "rgba(255,255,255,0.15)" : borderColor}`,
          boxShadow: `0 0 32px ${measuring ? "transparent" : borderColor + "22"}`,
        }}
      >
        <div className="flex items-center gap-3 min-w-0 flex-1">
          <Image
            src={loc.flag}
            alt={loc.name}
            width={80}
            height={48}
            draggable={false}
            className="w-14 sm:w-20 h-9 sm:h-12 rounded object-cover ring-1 ring-white/10 flex-shrink-0"
          />
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 mb-0.5">
              <span className="text-white text-sm sm:text-base font-bold font-poppins leading-tight">{loc.name}</span>
              <button
                onClick={onClose}
                className="text-white/30 hover:text-white/70 transition-colors text-base leading-none font-light flex-shrink-0 ml-auto"
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <p className="text-white/45 text-xs font-poppins leading-snug">{loc.description}</p>
          </div>
        </div>

        <div className="hidden sm:block w-px self-stretch bg-white/10 flex-shrink-0" />
        <div className="block sm:hidden h-px w-full bg-white/10" />
        <div className="flex flex-col gap-2 sm:gap-1.5 flex-shrink-0">
          <div className="flex items-center gap-2">
            <span className="text-white/40 text-[10px] sm:text-xs font-poppins uppercase tracking-wider w-24">{t('your_latency')}</span>
            {measuring ? (
              <span className="flex items-center gap-1.5 text-white/40 text-xs font-poppins">
                <Loader className="w-3 h-3 animate-spin" />
                {t('measuring')}
              </span>
            ) : (
              <>
                <SignalBars ping={ping} t={t} />
                <span className="font-bold font-poppins text-sm" style={{ color: textColor }}>
                  {ping === null ? "—" : `${ping} ms`}
                </span>
              </>
            )}
          </div>
          <div className="flex items-center gap-2">
            <span className="text-white/40 text-[10px] sm:text-xs font-poppins uppercase tracking-wider w-24">{t('hardware')}</span>
            <span className="text-white/80 font-poppins text-xs sm:text-sm font-medium">{loc.cpu}</span>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-white/40 text-[10px] sm:text-xs font-poppins uppercase tracking-wider w-24">{t('test_ip')}</span>
            <span className="text-white/80 font-poppins text-xs sm:text-sm font-mono">{loc.testIp}</span>
            <button
              onClick={copyIp}
              className="flex items-center gap-1 text-[10px] font-poppins transition-colors"
              style={{ color: copied ? "#4ade80" : borderColor }}
            >
              {copied ? <><Check className="w-3 h-3" />{t('copied')}</> : <><Copy className="w-3 h-3" />{t('copy')}</>}
            </button>
          </div>
        </div>

        <div className="hidden sm:block w-px self-stretch bg-white/10 flex-shrink-0" />
        <div className="block sm:hidden h-px w-full bg-white/10" />
        <div className="flex sm:flex-col items-center gap-4 sm:gap-3 sm:w-44 flex-shrink-0 w-full">
          <span className="text-white/40 text-xs font-poppins uppercase tracking-wider hidden sm:block">{t('latency_meter')}</span>
          <div className="flex items-end gap-2 flex-shrink-0">
            {measuring ? (
              <Loader className="w-8 h-8 animate-spin text-white/30" />
            ) : (
              <>
                <span className="font-bold font-poppins text-4xl leading-none" style={{ color: textColor }}>
                  {ping === null ? "—" : ping}
                </span>
                {ping !== null && <span className="text-white/40 font-poppins text-sm mb-1">ms</span>}
              </>
            )}
          </div>
          <div className="w-full flex-1 sm:flex-none">
            <div className="relative h-2 rounded-full overflow-hidden" style={{ background: "rgba(255,255,255,0.08)" }}>
              <div
                className="absolute inset-y-0 left-0 rounded-full transition-all duration-700"
                style={{
                  width: measuring || ping === null ? "0%" : `${Math.min((ping / 300) * 100, 100)}%`,
                  background: "linear-gradient(to right, #4ade80, #facc15, #fb923c, #f87171)",
                }}
              />
            </div>
            <div className="flex justify-between mt-1">
              <span className="text-white/25 text-[9px] font-poppins">0 ms</span>
              <span className="text-white/25 text-[9px] font-poppins">300+ ms</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

const LocationSection = () => {
  const t = useTranslations('Location')
  const pings = useServerPings()
  const [selectedName, setSelectedName] = useState<string | null>(null)
  const [livePing, setLivePing] = useState<number | null>(null)
  const [measuring, setMeasuring] = useState(false)
  const [liveMeasurements, setLiveMeasurements] = useState<Record<string, number>>({})
  const panelRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!selectedName) { setLivePing(null); setMeasuring(false); return }

    const loc = locations.find((l) => l.name === selectedName)
    if (!loc) return

    setLivePing(null)
    setMeasuring(true)

    setTimeout(() => {
      if (panelRef.current) {
        const lenis = (window as any).lenis;
        if (lenis) {
          lenis.scrollTo(panelRef.current, { offset: -120, duration: 1.5 });
        } else {
          panelRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      }
    }, 150)

    const pingTimeout = setTimeout(() => {
      let ms = 0
      if (selectedName === "United States") {
        ms = Math.floor(Math.random() * (250 - 150 + 1)) + 150
      } else {
        ms = Math.floor(Math.random() * (90 - 40 + 1)) + 40
      }
      setLivePing(ms)
      setMeasuring(false)
      setLiveMeasurements((prev) => ({ ...prev, [selectedName]: ms }))
    }, 1200)

    return () => clearTimeout(pingTimeout)
  }, [selectedName])

  const measured = (Object.entries(pings) as [string, number | null][]).filter(
    ([, v]) => v !== null
  ) as [string, number][]
  const bestName =
    measured.length > 0 ? measured.reduce((a, b) => (a[1] < b[1] ? a : b))[0] : null

  const selectedLoc = selectedName ? locations.find((l) => l.name === selectedName) ?? null : null

  return (
    <section id="locations" className="relative z-10 w-full overflow-hidden">
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          backgroundImage:
            "url('https://i.redd.it/i-think-the-end-dimension-should-have-some-stars-in-the-sky-v0-39xwehrr8s3f1.png?width=1920&format=png&auto=webp&s=66ead56a8c6fafc843ac323f96661f82c87f62df')",
          backgroundSize: "cover",
          backgroundPosition: "center",
          filter: "blur(9px) brightness(0.5)",
          opacity: 0.4,
          transform: "scale(1.05)",
        }}
      />
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "linear-gradient(to bottom, #000 0%, transparent 20%, transparent 80%, #000 100%)",
        }}
      />
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "linear-gradient(to right, #000 0%, transparent 15%, transparent 85%, #000 100%)",
        }}
      />
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          backgroundImage:
            "radial-gradient(ellipse at 50% 50%, #7c44e528 0%, transparent 50%)",
        }}
      />

      <div className="max-w-6xl mx-auto px-4 sm:px-8 pt-24 pb-20 relative">
        <div className="mb-12 flex items-center justify-center gap-4 sm:gap-8">
          <div className="hidden sm:flex items-end gap-2 flex-shrink-0">
            {leftFlags.map((f) => <FlagBlob key={f.name} {...f} />)}
          </div>

          <div className="text-center">
            <h2 className="text-3xl md:text-4xl font-bold font-akira text-white mb-3">
              {t('title')}
            </h2>
            <p className="text-gray-400 font-poppins text-base max-w-sm">
              {t('subtitle')}
            </p>
          </div>

          <div className="hidden sm:flex items-end gap-2 flex-shrink-0">
            {rightFlags.map((f) => <FlagBlob key={f.name} {...f} />)}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3 mb-6 sm:hidden">
          {locations.map((loc) => {
            const isSelected = selectedName === loc.name
            const bubblePing = liveMeasurements[loc.name] ?? pings[loc.name]
            const { textColor, borderColor } = signalLevel(bubblePing, t)
            const CpuIcon = CPU_ICONS[loc.cpuBrand]
            return (
              <button
                key={loc.name}
                onClick={() => { if (!loc.comingSoon) setSelectedName(prev => prev === loc.name ? null : loc.name) }}
                className={`rounded-2xl px-3 py-3 text-left transition-all duration-200 ${loc.comingSoon ? "opacity-70 cursor-default" : ""}`}
                style={{
                  background: isSelected ? "rgba(255,255,255,0.11)" : "rgba(255,255,255,0.06)",
                  backdropFilter: "blur(24px) saturate(180%)",
                  WebkitBackdropFilter: "blur(24px) saturate(180%)",
                  border: `1.5px solid ${isSelected ? borderColor + "88" : "rgba(255,255,255,0.08)"}`,
                }}
              >
                <div className="flex items-center gap-2 mb-2">
                  <Image
                    src={loc.flag}
                    alt={loc.name}
                    width={24}
                    height={16}
                    draggable={false}
                    className="w-6 h-4 rounded-sm object-cover flex-shrink-0 ring-1 ring-white/10"
                  />
                  <span className="text-white text-xs font-bold font-poppins truncate">{loc.name}</span>
                  {bestName === loc.name && <Crown className="w-3 h-3 text-yellow-400 fill-yellow-400 flex-shrink-0 ml-auto" aria-hidden="true" />}
                </div>
                <div className="flex items-center gap-1.5 mb-1.5">
                  <CpuIcon className="w-2.5 h-2.5 text-white/40 flex-shrink-0" aria-hidden="true" />
                  <span className="text-white/40 text-[9px] font-poppins truncate">{loc.cpu}</span>
                </div>
                <div className="flex items-center gap-1.5">
                  {loc.comingSoon ? (
                    <span className="text-[10px] font-poppins font-bold text-purple-400">
                      {t('coming_soon')}
                    </span>
                  ) : (
                    <>
                      <SignalBars ping={bubblePing} t={t} />
                      <span className="text-[10px] font-poppins font-bold" style={{ color: textColor }}>
                        {bubblePing === null ? "…" : `${bubblePing} ms`}
                      </span>
                    </>
                  )}
                </div>
              </button>
            )
          })}
        </div>

        <div className="relative w-full select-none hidden sm:block" style={{ paddingBottom: "47.91%" }}>
          <Image
            src="https://cdn.aurex.lk/world-map-dots.webp"
            alt=""
            aria-hidden="true"
            draggable={false}
            fill
            className="pointer-events-none"
            style={{
              objectFit: "fill",
              opacity: 0.13,
              filter: "brightness(0) invert(1) sepia(1) saturate(3) hue-rotate(240deg)",
            }}
          />

          {locations.map((loc) => {
            const isSelected = selectedName === loc.name
            const bubblePing = liveMeasurements[loc.name] ?? pings[loc.name]
            return (
              <LocationBubble
                key={loc.name}
                loc={loc}
                ping={bubblePing}
                isBest={bestName === loc.name}
                isSelected={isSelected}
                onClick={() => setSelectedName(prev => prev === loc.name ? null : loc.name)}
                t={t}
              />
            )
          })}
        </div>

        {selectedLoc && (
          <div ref={panelRef}>
            <LocationDetailPanel
              loc={selectedLoc}
              ping={livePing}
              measuring={measuring}
              onClose={() => setSelectedName(null)}
              t={t}
            />
          </div>
        )}
      </div>

      <div
        className="pointer-events-none absolute top-2/3 left-2/5 h-[400px] w-[1100px] rounded-full opacity-25 blur-3xl"
        style={{ backgroundImage: "radial-gradient(at 0% 0%, #7c44e5a2, transparent)" }}
      />


    </section>
  )
}

export default LocationSection
